import {
  IconDefinition,
  faHeadset,
  faMagnifyingGlass,
  faPlay,
  faUserPlus,
  faVideo,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface QuickActionButtonProps {
  to: string;
  icon: IconDefinition;
  title: string;
  subtitle: string;
  shortcutKey: number;
  primary?: boolean;
  /** Tailwind gradient classes (e.g. "bg-gradient-to-br from-[#..] to-[#..]") for a non-primary gradient button. */
  gradientClass?: string;
  gradientShadowClass?: string;
  iconBoxClass?: string;
  cardHoverBorderClass?: string;
  badge?: ReactNode;
}

const QuickActionButton = ({
  to,
  icon,
  title,
  subtitle,
  shortcutKey,
  primary,
  gradientClass,
  gradientShadowClass,
  iconBoxClass,
  cardHoverBorderClass,
  badge,
}: QuickActionButtonProps) => {
  const isGradient = primary || !!gradientClass;

  return (
    <Link
      to={to}
      title={`${title} — ${subtitle}`}
      aria-label={`${title} — ${subtitle}. Shortcut ${shortcutKey}.`}
      className={`group relative flex shrink-0 items-center gap-2 whitespace-nowrap rounded-lg border px-2 py-2 text-sm font-bold transition-all duration-200 ease-out hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-[#0B1220] ${
        primary
          ? 'bg-gradient-to-br from-[#2563EB] to-[#7C3AED] border-transparent text-white shadow-[0_4px_14px_-4px_rgba(124,58,237,0.55)] hover:shadow-[0_6px_18px_-4px_rgba(124,58,237,0.75)] focus-visible:ring-white'
          : gradientClass
            ? `${gradientClass} border-transparent text-white ${gradientShadowClass ?? 'shadow-[0_4px_14px_-4px_rgba(6,182,212,0.5)] hover:shadow-[0_6px_18px_-4px_rgba(6,182,212,0.7)]'} focus-visible:ring-white`
            : `bg-white dark:bg-[#1B2544] border-[#E2E8F0] dark:border-[#2E3B63] text-[#162033] dark:text-[#F8FAFC] shadow-[0_2px_8px_rgba(15,23,42,0.05)] dark:shadow-[0_2px_8px_rgba(0,0,0,0.35)] hover:shadow-[0_4px_14px_-4px_rgba(15,23,42,0.15)] dark:hover:bg-[#212C52] focus-visible:ring-primary ${cardHoverBorderClass ?? ''}`
      }`}
    >
      <span
        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border transition-transform duration-200 ease-out group-hover:scale-110 ${
          isGradient
            ? 'bg-white/20 border-white/30 text-white'
            : (iconBoxClass ?? '')
        }`}
      >
        <FontAwesomeIcon icon={icon} className="text-[11px]" />
      </span>
      <span className="hidden xl:inline">{title}</span>
      {badge}
    </Link>
  );
};

interface LeftQuickActionsProps {
  /** Pass the real open-ticket count when that data is wired up; omit to hide the badge. */
  supportTicketOpenCount?: number;
  /** Pass true only when a live-class session is actually in progress; omit to hide the badge. */
  isLiveClassActive?: boolean;
}

export const LeftQuickActions = ({
  supportTicketOpenCount,
  isLiveClassActive,
}: LeftQuickActionsProps) => {
  return (
    <div
      aria-label="Quick actions"
      className="quick-actions-left ml-0 hidden md:flex min-w-0 shrink items-center gap-2 overflow-x-auto py-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      <QuickActionButton
        to="/livezoomvideo"
        icon={faVideo}
        title="Live Classes"
        subtitle="Streaming Room"
        shortcutKey={3}
        iconBoxClass="bg-[#ECFDF5] dark:bg-[#10B981]/10 border-[#A7F3D0] dark:border-[#10B981]/30 text-[#047857] dark:text-[#34D399]"
        cardHoverBorderClass="hover:border-[#10B981] dark:hover:border-[#34D399]/70"
        badge={
          isLiveClassActive ? (
            <span className="flex items-center gap-0.5 rounded bg-[#ECFDF5] px-1 py-px text-[9px] font-bold text-[#047857] dark:bg-[#10B981]/15 dark:text-[#34D399]">
              <span className="h-1 w-1 animate-pulse rounded-full bg-[#10B981]" />
              LIVE
            </span>
          ) : undefined
        }
      />
      <QuickActionButton
        to="/support/support-ticket"
        icon={faHeadset}
        title="Support Ticket"
        subtitle="Help Desk"
        shortcutKey={2}
        iconBoxClass="bg-[#FFFBEB] dark:bg-[#F59E0B]/10 border-[#FDE68A] dark:border-[#F59E0B]/30 text-[#B45309] dark:text-[#FBBF24]"
        cardHoverBorderClass="hover:border-[#F59E0B] dark:hover:border-[#FBBF24]/70"
        badge={
          typeof supportTicketOpenCount === 'number' &&
          supportTicketOpenCount > 0 ? (
            <span className="rounded bg-[#FFFBEB] px-1 py-px text-[9px] font-bold text-[#B45309] dark:bg-[#F59E0B]/15 dark:text-[#FBBF24]">
              {supportTicketOpenCount}
            </span>
          ) : undefined
        }
      />

      <QuickActionButton
        to="/settings/player-link"
        icon={faPlay}
        title="Player Link"
        subtitle="DRM & Keys"
        shortcutKey={1}
        iconBoxClass="bg-[#EEF2FF] dark:bg-[#6366F1]/10 border-[#C7D2FE] dark:border-[#6366F1]/30 text-[#4F46E5] dark:text-[#818CF8]"
        cardHoverBorderClass="hover:border-[#6366F1] dark:hover:border-[#818CF8]/70"
      />
    </div>
  );
};

export const RightQuickActions = () => {
  return (
    <div
      aria-label="Primary actions"
      className="quick-actions-right ml-0 flex min-w-0 shrink items-center gap-2 overflow-x-auto py-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      <QuickActionButton
        to="/support/studentsearch"
        icon={faMagnifyingGlass}
        title="Student Search"
        subtitle="Records & KYC"
        shortcutKey={5}
        gradientClass="bg-gradient-to-br from-[#22D3EE] to-[#0a5dc2]"
      />
      <QuickActionButton
        to="/AssignActivationKey"
        icon={faUserPlus}
        title="Enroll a Student"
        subtitle="New Admission"
        shortcutKey={4}
        primary
        // badge={
        //   <span className="rounded bg-white/25 px-1 py-px text-[9px] font-bold text-white">
        //     +NEW
        //   </span>
        // }
      />
    </div>
  );
};
